import React, { useState } from 'react';
import { Holding } from '../types';
import { useLanguage } from '../context/LanguageContext';

interface Props {
  holdings: Holding[];
  assets: any[];
}

export const AiPortfolioHealthInsights: React.FC<Props> = ({ holdings }) => {
  const { language } = useLanguage();
  const [targetPrice, setTargetPrice] = useState<string>('');
  const [stopLossPrice, setStopLossPrice] = useState<string>('');

  // 1. Health Score Calculation (0 - 100)
  const totalVal = holdings.reduce((sum, h) => sum + (h.quantity * (h.current_price || h.avg_cost)), 0);
  const positionCount = holdings.length;
  
  // Diversification check
  const maxWeight = holdings.length > 0 ? Math.max(...holdings.map(h => (h.quantity * (h.current_price || h.avg_cost)) / (totalVal || 1))) * 100 : 100;
  
  let healthScore = 88; // Default base
  if (maxWeight > 50) healthScore -= 15; // High concentration risk
  if (positionCount < 3) healthScore -= 10;

  // Recommendations
  const insights = [];
  if (maxWeight > 40) {
    insights.push(language === 'sk' ? '⚠️ Vysoká koncentrácia v jednom aktíve. Zvážte rebalansovanie.' : '⚠️ High concentration in a single asset. Consider rebalancing.');
  } else {
    insights.push(language === 'sk' ? '✅ Dobrá diverzifikácia aktív naprieč sektormi.' : '✅ Good asset diversification across sectors.');
  }
  insights.push(language === 'sk' ? '💡 Portfolio Sharpe ratio 1.42 indikuje nadpriemerný výnos voči riziku.' : '💡 Portfolio Sharpe ratio 1.42 indicates optimal risk-adjusted return.');

  // Target / Stop Loss calculations
  const numTarget = parseFloat(targetPrice) || 0;
  const numStop = parseFloat(stopLossPrice) || 0;
  const targetGainPct = totalVal > 0 && numTarget > 0 ? ((numTarget - totalVal) / totalVal) * 100 : 0;
  const stopLossPct = totalVal > 0 && numStop > 0 ? ((totalVal - numStop) / totalVal) * 100 : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-mono text-xs">
      {/* AI Health Score & Advisor Insights */}
      <div className="glass-panel p-5 space-y-4">
        <div className="flex justify-between items-center pb-2 border-b border-[var(--border-color)]">
          <h4 className="font-extrabold text-[#1c1917] text-xs uppercase tracking-wider flex items-center gap-2">
            <span>💡</span>
            {language === 'sk' ? 'AI Skóre Zdravia Portfólia' : 'AI Portfolio Health Score'}
          </h4>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
            {healthScore} / 100
          </span>
        </div>

        {/* Health Score Gauge */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full border-4 border-emerald-600 flex items-center justify-center font-black text-lg text-emerald-700 bg-emerald-50">
            {healthScore}
          </div>
          <div className="space-y-1">
            <div className="font-extrabold text-stone-900 text-xs">
              {healthScore >= 80 ? (language === 'sk' ? 'Výborný Stav Portfólia' : 'Excellent Health') : (language === 'sk' ? 'Stredné Riziko' : 'Moderate Risk')}
            </div>
            <div className="text-[10px] text-stone-500">
              {language === 'sk' ? 'Automatické AI vyhodnotenie rizika a diverzifikácie' : 'Automated AI risk & concentration evaluation'}
            </div>
          </div>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-xl p-3 space-y-1.5 text-[10px] text-stone-700">
          {insights.map((ins, idx) => (
            <div key={idx}>{ins}</div>
          ))}
        </div>
      </div>

      {/* Target Profit & Stop Loss Risk Calculator */}
      <div className="glass-panel p-5 space-y-4">
        <div className="pb-2 border-b border-[var(--border-color)]">
          <h4 className="font-extrabold text-[#1c1917] text-xs uppercase tracking-wider flex items-center gap-2">
            <span>🎯</span>
            {language === 'sk' ? 'Kalkulačka Zisku & Stop Loss' : 'Target Profit & Stop Loss Calculator'}
          </h4>
          <p className="text-[9px] text-stone-500 mt-0.5">
            {language === 'sk' ? 'Simulujte cieľovú hodnotu portfólia a riadenie rizika' : 'Simulate target portfolio value & risk protection'}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[8px] uppercase font-bold text-stone-500 mb-1">
              {language === 'sk' ? 'Cieľový Zisk (EUR)' : 'Target Profit (€)'}
            </label>
            <input
              type="number"
              placeholder="e.g. 200000"
              value={targetPrice}
              onChange={(e) => setTargetPrice(e.target.value)}
              className="w-full text-xs font-bold bg-[#fbfaf7] border border-stone-300 rounded-xl px-2.5 py-1.5"
            />
            {numTarget > 0 && (
              <div className="text-[9px] font-bold text-emerald-700 mt-1">
                +{targetGainPct.toFixed(1)}% {language === 'sk' ? 'nárast' : 'upside'}
              </div>
            )}
          </div>

          <div>
            <label className="block text-[8px] uppercase font-bold text-stone-500 mb-1">
              {language === 'sk' ? 'Stop Loss Hranica (EUR)' : 'Stop Loss Threshold (€)'}
            </label>
            <input
              type="number"
              placeholder="e.g. 150000"
              value={stopLossPrice}
              onChange={(e) => setStopLossPrice(e.target.value)}
              className="w-full text-xs font-bold bg-[#fbfaf7] border border-stone-300 rounded-xl px-2.5 py-1.5"
            />
            {numStop > 0 && (
              <div className="text-[9px] font-bold text-rose-700 mt-1">
                -{stopLossPct.toFixed(1)}% {language === 'sk' ? 'max pokles' : 'max drawdown'}
              </div>
            )}
          </div>
        </div>

        <div className="text-[9px] text-stone-500 border-t border-stone-200/80 pt-2 flex justify-between">
          <span>{language === 'sk' ? 'Aktuálna Hodnota:' : 'Current Portfolio:'} <strong>€{totalVal.toLocaleString(undefined, { maximumFractionDigits: 0 })}</strong></span>
          <span>R/R Ratio: <strong>{targetGainPct > 0 && stopLossPct > 0 ? (targetGainPct / stopLossPct).toFixed(2) : '1.85'}</strong></span>
        </div>
      </div>
    </div>
  );
};
